import React from 'react';
import { Link } from 'react-router-dom';
import { 
  ArrowLeft, ArrowRight, BookOpen, Clock, Target, Repeat, TrendingUp,
  CheckCircle, Quote, Award, Users, ExternalLink
} from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';

const QUADRANTS = [
  {
    num: '1',
    slug: 'before',
    label: 'Before',
    subtitle: 'Preparation & Readiness',
    description: 'How you prepare mentally before stepping onto the first tee sets the tone for everything that follows. Visualisation, intention-setting, and building a "safe place" mindset.',
    color: 'bg-[#2C5530]',
    textColor: 'text-[#2C5530]',
    borderColor: 'hover:border-[#2C5530]/40',
  },
  {
    num: '2',
    slug: 'during',
    label: 'During',
    subtitle: 'Execution & Focus',
    description: 'The moments of execution — your pre-shot routine, shot commitment, and the ability to stay present when it matters most. One shot at a time.',
    color: 'bg-[#BC562B]',
    textColor: 'text-[#BC562B]',
    borderColor: 'hover:border-[#BC562B]/40',
  },
  {
    num: '3',
    slug: 'in-between',
    label: 'In-Between',
    subtitle: 'Recovery & Reset',
    description: 'What happens between shots is where most mental damage — or most mental gain — occurs. Body language, letting go, and the "next opportunity" mindset.',
    color: 'bg-[#7C3AED]',
    textColor: 'text-[#7C3AED]',
    borderColor: 'hover:border-[#7C3AED]/40',
  },
  {
    num: '4',
    slug: 'after',
    label: 'After',
    subtitle: 'Reflection & Growth',
    description: 'How you process a round after it\'s over determines what you carry into the next one. Honest reflection, extracting lessons, and building momentum.',
    color: 'bg-[#1A1A1A]',
    textColor: 'text-[#1A1A1A]',
    borderColor: 'hover:border-[#1A1A1A]/30',
  },
];

const TESTIMONIALS = [
  {
    quote: 'If you\'re going to win this thing, you\'re going to have to make committed golf swings.',
    author: 'Phil Mickelson',
    context: 'On the mental shift that contributed to his PGA Championship victory',
  },
  {
    quote: 'Karl helped me when my concentration was all over the place. The tools he gave me were simple but they changed everything.',
    author: 'Louis Oosthuizen',
    context: '2010 Open Championship winner, 7-shot victory at St Andrews',
  },
];

const TheApproachPage = () => {
  return (
    <div className="min-h-screen bg-[#FAF9F6]">
      <Header />

      <main className="pt-24 pb-20">
        {/* Hero */}
        <section className="bg-[#1A1A1A] py-16 md:py-24">
          <div className="container mx-auto px-6 md:px-12 max-w-4xl">
            <Link to="/" className="inline-flex items-center gap-2 text-white/50 hover:text-white text-sm mb-8 transition-colors" data-testid="back-link">
              <ArrowLeft className="w-4 h-4" /> Back to Home
            </Link>
            <p className="text-[#BC562B] text-sm uppercase tracking-[0.2em] mb-4">The Approach</p>
            <h1 className="font-['Playfair_Display'] text-4xl sm:text-5xl lg:text-6xl font-medium text-white mb-6">
              Four Areas for Balanced Improvement
            </h1>
            <p className="text-white/70 text-base md:text-lg leading-relaxed max-w-2xl">
              Inspired by Karl Morris's Mind Factor principles — tried and tested with six major championship winners — this framework divides your mental game into four areas that together create lasting, balanced improvement.
            </p>
          </div>
        </section>

        <div className="container mx-auto px-6 md:px-12 max-w-4xl">

          {/* Introduction */}
          <section className="py-12 md:py-16" data-testid="intro-section">
            <div className="bg-white border border-[#E7E5E4] p-6 md:p-8">
              <h2 className="font-['Playfair_Display'] text-2xl md:text-3xl text-[#1A1A1A] mb-4">Why Four Areas?</h2>
              <p className="text-[#57534E] leading-relaxed mb-4">
                Most golfers only think about their mental game <em>during</em> a shot. But the reality is that your mental state is shaped by what happens <strong>before</strong> you arrive at the course, how you <strong>execute</strong> each shot, what you do <strong>in between</strong> shots, and how you <strong>reflect</strong> afterwards.
              </p>
              <p className="text-[#57534E] leading-relaxed mb-4">
                Karl Morris, who has worked with major winners including Darren Clarke, Graeme McDowell, and Louis Oosthuizen, developed this framework over 15 years of research and practical application. The principle is simple: <strong>working on all four areas diligently can elevate your performance to a new level</strong>, with benefits extending beyond golf.
              </p>
              <p className="text-[#57534E] leading-relaxed">
                You can start with any area — there's no required sequence. At Quiet Mind Golf, your Performance Scorecards are structured around these exact four phases, so every round you track builds your understanding of where your mental game is strong and where the opportunities lie.
              </p>
            </div>
          </section>

          {/* Stats */}
          <section className="pb-12 md:pb-16" data-testid="stats-section">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {[
                { icon: Award, stat: '6', label: 'Major winners coached' },
                { icon: Users, stat: '15+', label: 'Years of research' },
                { icon: Target, stat: '4', label: 'Performance areas' },
                { icon: TrendingUp, stat: '95%', label: 'Of golf is between shots' },
              ].map((item, i) => (
                <div key={i} className="bg-white border border-[#E7E5E4] p-4 md:p-6 text-center" data-testid={`stat-${i}`}>
                  <item.icon className="w-6 h-6 text-[#2C5530] mx-auto mb-3" />
                  <p className="font-['Playfair_Display'] text-2xl md:text-3xl text-[#1A1A1A] font-medium">{item.stat}</p>
                  <p className="text-xs md:text-sm text-[#737373] mt-1">{item.label}</p>
                </div>
              ))}
            </div>
          </section>

          {/* Testimonial */}
          <section className="pb-12 md:pb-16" data-testid="testimonial-section">
            <div className="bg-[#F2F0E9] border-l-4 border-[#BC562B] p-6 md:p-8">
              <Quote className="w-8 h-8 text-[#BC562B]/30 mb-4" />
              <blockquote className="font-['Playfair_Display'] text-lg md:text-xl text-[#1A1A1A] leading-relaxed mb-4">
                "{TESTIMONIALS[1].quote}"
              </blockquote>
              <p className="text-[#737373] text-sm">
                — <strong>{TESTIMONIALS[1].author}</strong>, {TESTIMONIALS[1].context}
              </p>
            </div>
          </section>

          {/* The Mind Factor Image with Clickable Areas */}
          <section className="pb-12 md:pb-16" data-testid="quadrants-section">
            <div className="relative w-full rounded-lg overflow-hidden shadow-lg">
              <img 
                src="https://customer-assets.emergentagent.com/job_ce3ebfde-a92e-4c8a-aabd-1eead688aaee/artifacts/s0q6fq8n_Mind%20Factor.png" 
                alt="The Mind Factor - The 4 key areas to train in golf" 
                className="w-full h-auto"
                data-testid="mind-factor-image"
              />
              {/* Clickable overlay areas */}
              <div className="absolute inset-0 flex">
                <Link to="/the-approach/before" className="w-1/4 h-full flex items-end justify-center pb-[8%] group" data-testid="click-before">
                  <span className="opacity-0 group-hover:opacity-100 transition-opacity bg-[#2C5530] text-white text-xs font-bold px-4 py-2 rounded-full shadow-lg">
                    Explore Before →
                  </span>
                </Link>
                <Link to="/the-approach/during" className="w-1/4 h-full flex items-end justify-center pb-[8%] group" data-testid="click-during">
                  <span className="opacity-0 group-hover:opacity-100 transition-opacity bg-[#2C5530] text-white text-xs font-bold px-4 py-2 rounded-full shadow-lg">
                    Explore During →
                  </span>
                </Link>
                <Link to="/the-approach/in-between" className="w-1/4 h-full flex items-end justify-center pb-[8%] group" data-testid="click-in-between">
                  <span className="opacity-0 group-hover:opacity-100 transition-opacity bg-[#2C5530] text-white text-xs font-bold px-4 py-2 rounded-full shadow-lg">
                    Explore In-Between →
                  </span>
                </Link>
                <Link to="/the-approach/after" className="w-1/4 h-full flex items-end justify-center pb-[8%] group" data-testid="click-after">
                  <span className="opacity-0 group-hover:opacity-100 transition-opacity bg-[#2C5530] text-white text-xs font-bold px-4 py-2 rounded-full shadow-lg">
                    Explore After →
                  </span>
                </Link>
              </div>
            </div>
          </section>

          {/* How It Connects */}
          <section className="pb-12 md:pb-16" data-testid="connection-section">
            <div className="bg-[#2C5530] p-6 md:p-10 text-white">
              <h2 className="font-['Playfair_Display'] text-2xl md:text-3xl mb-4 text-white">How This Works in Quiet Mind Golf</h2>
              <p className="text-white/80 leading-relaxed mb-6">
                Your Performance Scorecards are built around these exact four areas. Every time you complete a scorecard, you rate yourself across Before, During, In-Between, and After — creating a data-driven picture of your mental game over time.
              </p>
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="bg-white/10 p-4">
                  <p className="text-white/60 text-xs uppercase tracking-wider mb-2">Performance Scorecards</p>
                  <p className="text-white/90 text-sm">Rate yourself 1-10 across 16 criteria mapped to all four areas. Track trends over weeks and months.</p>
                </div>
                <div className="bg-white/10 p-4">
                  <p className="text-white/60 text-xs uppercase tracking-wider mb-2">Session Logging</p>
                  <p className="text-white/90 text-sm">Record mood before and after sessions. Track how preparation affects performance outcomes.</p>
                </div>
                <div className="bg-white/10 p-4">
                  <p className="text-white/60 text-xs uppercase tracking-wider mb-2">Personal Diary</p>
                  <p className="text-white/90 text-sm">Reflect on each area after rounds. Identify patterns in your mental game across all four phases.</p>
                </div>
                <div className="bg-white/10 p-4">
                  <p className="text-white/60 text-xs uppercase tracking-wider mb-2">Reports & Insights</p>
                  <p className="text-white/90 text-sm">Radar charts, trend lines, and averages reveal which area of your game needs the most attention.</p>
                </div>
              </div>
            </div>
          </section>

          {/* Second Testimonial */}
          <section className="pb-12 md:pb-16" data-testid="testimonial-2">
            <div className="bg-white border border-[#E7E5E4] p-6 md:p-8">
              <Quote className="w-8 h-8 text-[#2C5530]/20 mb-4" />
              <blockquote className="text-[#1A1A1A] text-base md:text-lg leading-relaxed mb-4 italic">
                "{TESTIMONIALS[0].quote}"
              </blockquote>
              <p className="text-[#737373] text-sm">
                — <strong>{TESTIMONIALS[0].author}</strong>, {TESTIMONIALS[0].context}
              </p>
            </div>
          </section>

          {/* Attribution */}
          <section className="pb-12 md:pb-16" data-testid="attribution-section">
            <div className="bg-[#F2F0E9] p-6 md:p-8 flex flex-col md:flex-row items-start gap-6">
              <a href="https://themindfactor.net/" target="_blank" rel="noopener noreferrer" className="flex-shrink-0">
                <img src="https://customer-assets.emergentagent.com/job_35784d2f-aced-4c04-9d93-c997734de577/artifacts/mois1avk_MF001-300%20dpi.jpg" alt="The Mind Factor - Train Your Golf Brain" className="h-20 w-auto" />
              </a>
              <div>
                <h3 className="font-['Playfair_Display'] text-xl text-[#1A1A1A] mb-2">Inspired by The Mind Factor</h3>
                <p className="text-[#57534E] text-sm leading-relaxed mb-3">
                  This framework is inspired by Karl Morris's Mind Factor principles — a body of work developed over 15+ years with players at every level, from club golfers to major champions. Karl's approach emphasises practical, applicable tools over theory.
                </p>
                <a
                  href="https://themindfactor.net/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-sm text-[#2C5530] font-semibold hover:underline"
                  data-testid="mind-factor-link"
                >
                  Visit themindfactor.net <ExternalLink className="w-3.5 h-3.5" />
                </a>
              </div>
            </div>
          </section>

          {/* CTA */}
          <section className="pb-8" data-testid="cta-section">
            <div className="bg-[#1A1A1A] p-8 md:p-12 text-center">
              <h2 className="font-['Playfair_Display'] text-2xl md:text-3xl text-white mb-4">Start Tracking All Four Areas</h2>
              <p className="text-white/60 mb-8 max-w-lg mx-auto">Your Performance Scorecards are built around Before, During, In-Between, and After. Create your free account and start building your mental game picture.</p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link to="/register" className="px-8 py-3 bg-[#BC562B] text-white font-semibold hover:bg-[#9A4520] transition-colors" data-testid="cta-register">Start Free Account</Link>
                <Link to="/mac-research" className="px-8 py-3 border border-white/30 text-white font-semibold hover:bg-white/10 transition-colors" data-testid="cta-mac">Explore QMG Blueprint</Link>
              </div>
            </div>
          </section>

        </div>
      </main>

      <Footer />
    </div>
  );
};

export default TheApproachPage;
